import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useEffect } from 'react';
import { ADD_FAVORITE, REMOVE_FAVORITE, GET_MY_FAVORITES } from '../services/carService/queries';
import { toast } from 'react-toastify';
import { authStore } from '../../store/authStore';

interface UseFavoritesReturn {
  addFavorite: (carId: string) => Promise<void>;
  removeFavorite: (carId: string) => Promise<void>;
  toggleFavorite: (carId: string, isCurrentlyFavorite: boolean) => Promise<void>;
  favorites: any[];
  loading: boolean;
  error: any;
}

export const useFavorites = (): UseFavoritesReturn => {
  const client = useApolloClient();
  const isAuthenticated = authStore.isAuthenticated;

  // Query for favorites list
  const { data, loading, error, refetch } = useQuery(GET_MY_FAVORITES, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      // Silently fail if not authenticated
      if (!error.message.includes('Unauthorized')) {
        // Handle other errors if needed
      }
    }
  }, [error]);

  // Add favorite mutation
  const [addFavoriteMutation] = useMutation(ADD_FAVORITE, {
    onCompleted: () => {
      // Refetch favorites to update the list
      refetch();
      // Update cache for car queries to reflect isFavorite status
      updateCarCache(true);
    },
    onError: error => {
      toast.error('Failed to add favorite. Please try again.');
    },
  });

  // Remove favorite mutation
  const [removeFavoriteMutation] = useMutation(REMOVE_FAVORITE, {
    onCompleted: () => {
      // Refetch favorites to update the list
      refetch();
      // Update cache for car queries to reflect isFavorite status
      updateCarCache(false);
    },
    onError: error => {
      toast.error('Failed to remove favorite. Please try again.');
    },
  });

  // Helper function to update car cache
  const updateCarCache = (isFavorite: boolean) => {
    try {
      // Update isFavorite in various car queries cache
      // This is a simple approach - in production you might want to use cache.modify for more control
      client.cache.evict({ fieldName: 'cars' });
      client.cache.evict({ fieldName: 'car' });
      client.cache.evict({ fieldName: 'availableCars' });
      client.cache.evict({ fieldName: 'getCarsNearMe' });
      client.cache.gc();
    } catch (err) {}
  };

  const addFavorite = async (carId: string): Promise<void> => {
    if (!isAuthenticated) {
      toast.error('Please log in to add favorites');
      return;
    }

    try {
      await addFavoriteMutation({
        variables: {
          input: { carId },
        },
        optimisticResponse: {
          addFavorite: {
            __typename: 'Favorite',
            id: `temp-${carId}`,
            userId: authStore.user?.id || '',
            carId,
            createdAt: new Date().toISOString(),
          },
        },
        // Optimistically update cache
        update: cache => {
          updateCarCache(true);
        },
      });
      toast.success('Added to favorites');
    } catch (error: any) {
      // Error handling is done in onError
      throw error;
    }
  };

  const removeFavorite = async (carId: string): Promise<void> => {
    if (!isAuthenticated) {
      toast.error('Please log in to manage favorites');
      return;
    }

    try {
      await removeFavoriteMutation({
        variables: {
          input: { carId },
        },
        // Optimistically update cache
        update: cache => {
          updateCarCache(false);
        },
      });
      toast.success('Removed from favorites');
    } catch (error: any) {
      // Error handling is done in onError
      throw error;
    }
  };

  const toggleFavorite = async (carId: string, isCurrentlyFavorite: boolean): Promise<void> => {
    if (!isAuthenticated) {
      toast.error('Please log in to manage favorites');
      return;
    }

    if (isCurrentlyFavorite) {
      await removeFavorite(carId);
    } else {
      await addFavorite(carId);
    }
  };

  return {
    addFavorite,
    removeFavorite,
    toggleFavorite,
    favorites: data?.getMyFavorites || [],
    loading,
    error,
  };
};
